import ExpandMore from '@mui/icons-material/ExpandMore';
import { alpha, createTheme, darken } from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { BASE_INPUT_HEIGHT, TOP_BAR_HEIGHT } from '@openx/constants/styles.ts';
import { X } from '@phosphor-icons/react';
import { createElement } from 'react';
import { ELEVATION } from './elevation.ts';
import { palette } from './paletteV2.ts';
import { typography, WEIGHT_SEMIBOLD } from './typography.ts';

export const CommonInputStyles = {
  borderRadius: 2,
  backgroundColor: palette.common.white,
  border: `1px solid ${palette.divider}`,
  '&:not(.Mui-disabled):not(.Mui-error):hover': {
    borderColor: palette.divider,
    backgroundColor: palette.background.boxLight,
  },
  '&:focus': {
    backgroundColor: palette.common.white,
    borderColor: palette.divider,
  },
};

const InputErrorStyles = {
  backgroundColor: palette.error.light,
  borderColor: palette.error.main,
  '&:hover': {
    backgroundColor: palette.error.light,
    borderColor: palette.divider,
  },
};

const InputDisableStyles = {
  borderRadius: 2,
  backgroundColor: palette.background.boxDarker,
  color: palette.text.secondary,
};

export const theme = createTheme({
  palette,
  typography,
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          borderBlock: `1px solid ${palette.divider}`,
          boxSizing: 'border-box',
          height: TOP_BAR_HEIGHT,
        },
      },
    },
    MuiTabs: {
      defaultProps: {
        indicatorColor: 'primary',
      },
      styleOverrides: {
        root: {
          minHeight: 44,
          background: palette.background.paper,
          borderBottom: `1px solid ${palette.divider}`,
        },
        list: {
          padding: '0 32px',
        },
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          ...typography.body1Bold,
          textTransform: 'none',
          padding: '8px 16px',
          margin: 0,
          minHeight: 43,
          minWidth: '60px !important',
          transition: 'background 0.3s ease-in-out',
          color: palette.text.primary,
          '&:hover': {
            background: palette.background.boxDarker,
          },
          '&.Mui-selected': {
            color: palette.text.primary,
          },
          '&.Mui-disabled': {
            color: palette.action.disabled,
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: TOP_BAR_HEIGHT,
        },
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: createElement(ExpandMore),
      },
      styleOverrides: {
        root: {
          '&.Mui-expanded': {
            minHeight: '48px',
          },
        },
        content: {
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none !important',
          borderRadius: 2,
          padding: '6px 12px',
          '&.MuiButton-contained': {
            color: palette.common.white,

            '&.Mui-disabled': {
              color: palette.background.boxDarker,
              backgroundColor: palette.action.disabled,
            },
          },
          '&.MuiButton-sizeSmall': {
            padding: '4px 8px',
            fontSize: 12,
          },
          '&.MuiButton-sizeLarge': {
            padding: '8px 16px',
            fontSize: 14,
            lineHeight: '22px',
          },
          '&.MuiButton-text&.MuiButton-sizeSmall': {
            padding: '4px',
            fontSize: 12,
          },
          '&.MuiButton-text': {
            padding: '6px',
            minWidth: 'auto',
          },
          // Text secondary is the neutral grey label, as in the DS (contained secondary stays navy).
          // A `variants` entry keeps the specificity low, so component styles and sx still win.
          variants: [
            {
              props: { variant: 'text', color: 'secondary' },
              style: {
                color: palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(palette.common.black, 0.04),
                },
              },
            },
          ],
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          height: 32,
          width: 32,
          '& .MuiSvgIcon-root': {
            height: 18,
            width: 18,
          },
          '&.MuiAutocomplete-clearIndicator, &.MuiAutocomplete-popupIndicator': {
            height: 28,
            width: 28,
          },
          // Secondary icon button is the neutral grey icon, as in the DS.
          variants: [
            {
              props: { color: 'secondary' },
              style: {
                color: palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(palette.common.black, 0.04),
                },
              },
            },
          ],
        },
        sizeSmall: {
          height: 28,
          width: 28,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${palette.divider}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover': {
            '&:hover': {
              backgroundColor: palette.background.boxDark,
              borderColor: palette.background.boxDark,
            },
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          ...typography.body1,
          borderBottom: `1px solid ${palette.divider}`,
          height: 44,
          wordBreak: 'break-all',
          padding: 0,
          paddingLeft: 10,
          paddingRight: 10,
          lineHeight: '22px',
        },
        head: {
          height: 50,
          fontWeight: 600,
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          color: palette.common.white,
        },
        li: {
          opacity: 0.8,
          '& > a': {
            color: palette.common.white,
          },
          '&:last-child': {
            opacity: 1,
          },
        },
        separator: {
          opacity: 0.8,
          '&:nth-last-of-type(2)': {
            opacity: 1,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.MuiInputBase-root.Mui-disabled': {
            ...InputDisableStyles,
            '& input, & textarea': {
              ...InputDisableStyles,
              WebkitTextFillColor: palette.text.secondary,
            },
          },
          '&.MuiInputBase-root.Mui-error': {
            ...InputErrorStyles,
            '& input, & textarea': {
              ...InputErrorStyles,
            },
          },
        },
        multiline: {
          padding: 0,
          lineHeight: '30px',
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          '&.MuiPickersInputBase-root.Mui-disabled': {
            ...InputDisableStyles,
          },
          '&.MuiPickersInputBase-root.Mui-error': {
            ...InputErrorStyles,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          ...typography.body1,
          ...CommonInputStyles,
          '&::-webkit-search-cancel-button': {
            display: 'none',
          },
          padding: '0 12px',
          boxSizing: 'border-box',
          color: palette.text.primary,
          '&.Mui-disabled': {
            padding: '2px 8px 2px 12px',
          },
          '&::placeholder': {
            color: palette.text.disabled,
          },
          minHeight: BASE_INPUT_HEIGHT,
        },
        inputTypeSearch: {
          padding: '2px 32px 2px 12px',
        },
        multiline: {
          lineHeight: '30px',
          '&>textarea': {
            lineHeight: '30px',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root+.MuiInput-root': {
            marginTop: 18,
          },
        },
        input: {
          border: 'none',
          minHeight: 30,
          maxHeight: 30,
        },
        inputRoot: {
          ...CommonInputStyles,
          paddingLeft: 12,
          '&.Mui-error .MuiInputBase-input': {
            ...InputErrorStyles,
          },
          '&.MuiInput-root': {
            paddingBottom: 0,
            '&.Mui-disabled': {
              ...InputDisableStyles,
              '& input, & textarea': {
                ...InputDisableStyles,
                WebkitTextFillColor: palette.text.secondary,
              },

              '&:hover input, &:hover textarea': {
                ...InputDisableStyles,
              },
            },
            '&:hover input, &:hover textarea': {
              borderColor: palette.divider,
              backgroundColor: palette.background.boxDark,
            },
          },
        },
        tag: {
          ...typography.body2Bold,
          height: 28,
          padding: '3px 2px 3px 12px',
          margin: '1px 4px 1px 0px',
          '& .MuiChip-deleteIcon': {
            width: 22,
            height: 22,
          },
        },
        endAdornment: {
          paddingRight: 8,
        },
        option: {
          height: 36,
          // Hover is a neutral overlay and selected is primary.light, as in the DS.
          '&:hover': {
            backgroundColor: `${alpha(palette.common.black, 0.04)} !important`,

            '&[aria-selected=true]': {
              backgroundColor: `${palette.primary.light} !important`,
            },
          },
          '&[aria-selected=true]': {
            backgroundColor: `${palette.primary.light} !important`,
          },
        },
        popupIndicator: {
          '&:hover': {
            background: palette.background.boxDarker,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          ...typography.caption,
          color: palette.text.secondary,
          marginLeft: 12,
          '&.Mui-focused': {
            color: palette.text.secondary,
          },
        },
        shrink: {
          transform: 'translate(0)',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 5,
          '& svg': {
            width: 18,
            height: 18,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 5,
          '& svg': {
            width: 18,
            height: 18,
          },
        },
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          '& .MuiFormGroup-chip': {
            backgroundColor: palette.background.boxLight,
            border: `1px solid ${palette.divider}`,
            borderRadius: 8,
            height: '100%',
            margin: 0,
            padding: 8,
            width: '100%',
          },
          '& .MuiFormGroup-chip:has(.Mui-checked)': {
            backgroundColor: palette.primary.light,
            border: `1px solid ${palette.primary.border}`,
          },
          '& .MuiFormGroup-chip.Mui-disabled': {
            opacity: 0.5,
          },
          '& .MuiFormGroup-chip.Mui-disabled:has(.Mui-checked)': {
            opacity: 1,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          ...typography.caption,
          marginLeft: 12,
          marginTop: 2,
          '&.Mui-error': {
            '&:first-letter': {
              textTransform: 'uppercase',
            },
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: -5,
        },
        label: {
          fontSize: 13,
          lineHeight: '28px',
          marginLeft: 4,
          width: '100%',

          '&.Mui-disabled': {
            color: palette.text.primary,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          marginLeft: 12,
          fontSize: 12,
          lineHeight: '16px',
          color: palette.text.secondary,
          '&.Mui-focused': {
            color: palette.text.secondary,
          },
        },
      },
    },
    MuiChip: {
      defaultProps: {
        // Plain Phosphor X instead of MUI's filled Cancel circle.
        deleteIcon: createElement(X),
      },
      styleOverrides: {
        root: {
          ...typography.body1Bold,
          height: 32,
          minWidth: 32,
          padding: '2px 2px 2px 14px',
          margin: '0 8px 4px 0',
          backgroundColor: palette.background.antiflashWhite,
          color: palette.text.primary,
          borderRadius: 50,
          '&:hover': {
            backgroundColor: palette.background.grayLight,
          },
        },
        sizeSmall: {
          ...typography.body2Bold,
          height: 28,
          padding: '3px 3px 3px 12px',
          margin: '0 4px 4px 0',
          minWidth: 28,
          '& .MuiChip-deleteIcon': {
            width: 22,
            height: 22,
            color: palette.text.secondary,
            margin: '0 0 0 10px',
            // Default X icon only; custom IconButton delete icons keep their 22px hit area.
            '&:not(.MuiIconButton-root)': {
              width: 14,
              height: 14,
              margin: '0 7px 0 10px',
            },
            '&:hover': {
              borderRadius: 50,
              color: palette.text.primary,
            },
          },
        },
        label: {
          paddingLeft: 0,
          paddingRight: 0,
        },
        deleteIcon: {
          width: 28,
          height: 28,
          color: palette.text.secondary,
          margin: '0 0 0 4px',
          // Default X icon only; custom IconButton delete icons keep their 28px hit area.
          '&:not(.MuiIconButton-root)': {
            width: 16,
            height: 16,
            margin: '0 8px 0 10px',
          },
          '&:hover': {
            borderRadius: 50,
            color: palette.text.primary,
          },
        },
        deletable: {
          '&:hover': {
            backgroundColor: palette.background.grayLight,
          },
          '&:focus': {
            backgroundColor: palette.background.antiflashWhite,
          },
        },
        clickable: {
          '&:hover': {
            backgroundColor: palette.background.grayLight,
          },
          '&:focus': {
            backgroundColor: palette.background.antiflashWhite,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '.MuiYearCalendar-button': {
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.75,
          },
        },
        elevation8: {
          boxShadow: ELEVATION.elevation8,
        },
        elevation0: {
          boxShadow: ELEVATION.elevation0,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
        },
        rounded: {
          borderRadius: 2,
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        disableInteractive: true,
      },
      styleOverrides: {
        tooltip: {
          ...typography.caption,
          borderRadius: 2,
          padding: '6px 8px',
          color: palette.common.white,
          backgroundColor: palette.background.tooltip,
          margin: '0 !important',
        },
        tooltipPlacementLeft: {
          marginRight: '8px !important',
        },
        tooltipPlacementRight: {
          marginLeft: '8px !important',
        },
        tooltipPlacementTop: {
          marginBottom: '8px !important',
        },
        tooltipPlacementBottom: {
          marginTop: '8px !important',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 2,
          width: '100%',
          boxShadow: ELEVATION.elevation8,
        },
        paperWidthSm: {
          maxWidth: '672px',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          fontWeight: WEIGHT_SEMIBOLD,
          fontSize: 16,
          lineHeight: '26px',
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: palette.text.primary,
          marginBottom: 0,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '&:first-of-type': {
            paddingTop: '24px',
          },
        },
        dividers: {
          padding: '30px 16px 0 16px',
          borderBottom: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          padding: '12px 24px',
          maxWidth: '66.6%',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          color: palette.secondary.main,
          background: 'none',
          lineHeight: '16px',
          fontSize: '10px',
          padding: '0px 4px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: palette.primary.light,
            color: palette.common.black,
          },
          '&.danger': {
            color: palette.error.main,
            '&:hover': {
              color: palette.common.white,
              backgroundColor: palette.error.main,
            },
          },
          '&.Mui-selected': {
            backgroundColor: palette.background.boxDarker,
            '&:hover': {
              backgroundColor: palette.primary.main,
              color: palette.common.white,
            },
          },
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          color: palette.text.primary,
        },
        message: {
          ...typography.body1,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          flexDirection: 'row',
          boxShadow: 'none',
          padding: '12px 16px',
          ...typography.body1,
          '&.MuiAlert-standard.MuiAlert-colorWarning': {
            backgroundColor: palette.warning.light,

            '& .MuiAlert-icon': {
              color: palette.warning.main,
            },
          },
          '&.MuiAlert-standard.MuiAlert-colorSuccess': {
            backgroundColor: palette.success.light,

            '& .MuiAlert-icon': {
              color: palette.success.main,
            },
          },
          '&.MuiAlert-standard.MuiAlert-colorInfo': {
            backgroundColor: palette.primary.light,

            '& .MuiAlert-icon': {
              color: palette.primary.main,
            },
          },
          '&.MuiAlert-standard.MuiAlert-colorError': {
            backgroundColor: palette.error.light,

            '& .MuiAlert-icon': {
              color: palette.error.main,
            },
          },
        },
        icon: {
          padding: '3px 0',
        },
        message: {
          color: palette.text.primary,
          padding: 0,
        },
        action: {
          marginRight: 0,
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          marginTop: 0,
          marginBottom: 0,
          fontWeight: 600,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 28,
          height: 18,
          padding: 0,
          margin: 8,
          overflow: 'visible',
        },
        switchBase: {
          padding: 5,
          marginTop: -5,
          marginLeft: -5,
          width: 'auto',
          height: 'auto',
          '&.Mui-disabled': {
            color: `${palette.background.boxDarker}!important`,
            '& + .MuiSwitch-track': {
              backgroundColor: `${palette.action.disabled}!important`,
              opacity: 1,
            },
          },
          '&.Mui-checked': {
            transform: 'translateX(10px)',
            color: `${palette.background.boxDarker}!important`,
            '& + .MuiSwitch-track': {
              opacity: 1,
            },
          },
        },
        thumb: {
          width: 18,
          height: 18,
          boxShadow: ELEVATION.elevation16,
        },
        track: {
          borderRadius: 18 / 2,
          backgroundColor: palette.action.disabled,
          opacity: 1,
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          height: 30,
        },
        middleButton: {
          border: 'none',
        },
        lastButton: {
          border: 'none',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          fontSize: 13,
          lineHeight: '20px',
          fontWeight: 600,
          border: 'none',
          color: palette.text.secondary,
          backgroundColor: palette.background.grayLight,
          padding: '6px 12px',
          '&:hover': {
            border: 'none',
            backgroundColor: darken(palette.background.grayLight, 0.16),
          },
          '&.Mui-selected': {
            backgroundColor: palette.primary.main,
            color: palette.common.white,
            '&:hover': {
              backgroundColor: palette.primary.main,
            },
          },
          '&.Mui-disabled': {
            backgroundColor: palette.background.boxDarker,
            color: darken(palette.text.disabled, 0.16),
            '&.Mui-selected': {
              backgroundColor: darken(palette.text.disabled, 0.16),
              color: palette.background.boxDarker,
            },
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        rail: {
          height: 4,
          opacity: 0.15,
        },
        mark: {
          height: 4,
          width: 4,
          borderRadius: 2,
        },
        thumb: {},
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          background: alpha(palette.common.black, 0),
          width: '100%',
          padding: 0,
          border: 'none',
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        root: {
          '&.Mui-active': {
            '& .MuiStepConnector-line': {
              opacity: 1,
            },
          },
          '&.Mui-completed': {
            '& .MuiStepConnector-line': {
              opacity: 1,
            },
          },
        },
        line: {
          borderColor: palette.common.white,
          opacity: 0.4,
          borderTopWidth: 1,
          borderRadius: 1,
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        horizontal: {
          paddingLeft: 12,
          paddingRight: 12,
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          ...typography.body1,
          lineHeight: '18px',
          color: alpha(palette.background.paper, 0.8),

          '&.Mui-active': {
            color: `${palette.common.white} !important`,
          },
          '&.Mui-completed': {
            color: `${palette.common.white} !important`,
          },
        },
        labelContainer: {
          color: palette.common.white,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        },
        iconContainer: {
          paddingRight: 12,
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          border: `1px solid ${alpha(palette.background.paper, 0.4)}`,
          zIndex: 1,
          color: 'transparent',
          width: 32,
          height: 32,
          display: 'flex',
          borderRadius: '50%',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'none',
          '&.Mui-active': {
            color: palette.common.white,
            border: 'none',
            '& text': {
              fill: palette.primary.main,
            },
          },
          '&.Mui-completed': {
            color: palette.common.white,
            border: 'none',
            '& text': {
              fill: palette.primary.main,
            },
          },
        },
        text: {
          fontSize: 10,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '20px',
          display: 'block',
          fontSize: typography.body1.fontSize,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          ...CommonInputStyles,
          minHeight: BASE_INPUT_HEIGHT,

          '& input': {
            lineHeight: '18px',
            height: '30px',
          },
        },
        input: {
          boxSizing: 'border-box',
          paddingBlock: 0,
        },
        notchedOutline: {
          border: 'none',
        },
        adornedEnd: {
          paddingRight: 8,
        },
        adornedStart: {
          paddingLeft: 12,
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          ...CommonInputStyles,
          minHeight: BASE_INPUT_HEIGHT,
          paddingRight: '8px',
          // MUI X sets `borderStyle: solid` on the focused outline (unlike MuiOutlinedInput), which re-enables the hidden notchedOutline
          '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
            borderStyle: 'none',
          },
        },
        sectionsContainer: {
          padding: '8px 0',
        },
        notchedOutline: {
          border: 'none',
        },
      },
    },
    // TODO(UIG-1129): remove when apps switch to the @openx/design-system theme (same mapping as the DS)
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          body1Bold: 'p',
          body2Bold: 'p',
          display: 'p',
          displaySubtitle: 'p',
        },
      },
    },
  },
});
